import React, { startTransition } from "react";
import { navigate } from "gatsby";

import { ApplicationContext } from "src/context";

import LoginModal from "src/components/loginModal";
import { ToastBarV2 } from "src/modules/toastBar";
import Toolbar, { ToolbarMobileV2 } from "src/layouts/components/toolbar";
import NavigationBar from "src/layouts/components/navigationbar";
import MobileBackdrop, { MobileNavigation } from "src/layouts/components/mobilemenu";
import MobileToolbarFooterWrapper from "src/layouts/components/toolbar/mobile.toolbar.footer.wrapper";
import NavigationFooter from "src/layouts/components/navigationfooter";
import Socket from "src/components/socket";
import FlyoutPromoBox from "src/modules/flyoutPromoBox";
import BackToTopButton from "src/modules/backToTopButton/back.to.top.button";
import InAppBackButton from "src/modules/inAppBackButton";
import FooterModule from "src/modules/footer";

// commerce - login - modal
import provisionalModuleDataBasedOnLanguage from "src/modules/ecommerceSection/loginRegister/provisionalModuleDataBasedOnLanguage"

// TODO : These must be placed on page context
import pages from "src/../src-dynamic/destination.pages";
import { defaultCurrency, currencies } from "src/../src-dynamic/destination.currencies";
import { languages } from "src/../src-dynamic/destination.languages";


type StandardLayoutProps = {
  destinationId: string;
  language: string;
  defaultLanguage: string;
  location: any;
  pageContext: TPagePageContext; // | TEntryPageContext | TOtherPageContext;
  children?: React.ReactNode
}

type StandardLayoutState = {
  scrolledDown: boolean;
  mobileMenuOpen: boolean;
  showLoginModal: boolean;
  timestamp: number;
}


export default class StandardLayout extends React.Component<StandardLayoutProps, StandardLayoutState> {

  /** @ts-ignore */
  context!: React.ContextType<typeof ApplicationContext>;
  static contextType = ApplicationContext;

  public state: StandardLayoutState = {
    scrolledDown: false,
    mobileMenuOpen: false,
    showLoginModal: false,
    timestamp: 0,
  }

  static TIMESPAN_PROCESS_SCROLL_EVENTS = 10;
  static SCROLLGAP_NEEDED_FOR_OPEN  = 90;
  static SCROLLGAP_NEEDED_FOR_CLOSE = 30;

  private scrollHandler = () => {
    const now = new Date().getTime();
    const valid = now - this.state.timestamp > StandardLayout.TIMESPAN_PROCESS_SCROLL_EVENTS;
    if (!valid) return;
    if (this.state.scrolledDown) {
      const scrolled = !(window.scrollY < StandardLayout.SCROLLGAP_NEEDED_FOR_CLOSE);
      this.setState({scrolledDown: scrolled, timestamp: new Date().getTime()});
    } else {
      const scrolled = window.scrollY > StandardLayout.SCROLLGAP_NEEDED_FOR_OPEN;
      this.setState({scrolledDown: scrolled, timestamp: new Date().getTime()});
    }
  }

  toggleMobileMenu = () => {
    this.setState({mobileMenuOpen: !this.state.mobileMenuOpen});
  }

  closeMobileMenu = () => {
    if (this.state.mobileMenuOpen) this.setState({mobileMenuOpen: false});
  }

  toggleLoginModal = (open: boolean = !this.state.showLoginModal) => {
    this.setState({showLoginModal: open});
  }

  shouldComponentUpdate(nextProps: Readonly<StandardLayoutProps>, nextState: Readonly<StandardLayoutState>, nextContext: any): boolean {
    if (!this.props.location || !nextProps.location) return true;
    if (this.props.location.pathname !== nextProps.location.pathname) return true;
    if (this.props.pageContext !== nextProps.pageContext) return true;
    if (this.state.scrolledDown !== nextState.scrolledDown) return true;
    if (this.state.showLoginModal !== nextState.showLoginModal) return true;
    if (this.state.mobileMenuOpen !== nextState.mobileMenuOpen) return true;
    if (this.props.language !== nextProps.language) return true;
    return false;
  }

  componentDidUpdate(prevProps: Readonly<StandardLayoutProps>, prevState: Readonly<StandardLayoutState>, snapshot?: any): void {
    if (!prevProps.location) return;

    if (prevProps.location.pathname !== this.props.location.pathname && this.state.mobileMenuOpen === true) {
      this.closeMobileMenu();
    }
  }

  componentDidMount() {
    startTransition(() => {
      const { layout } = this.props.pageContext;
      const { navigation_bar, back_to_top_button } = layout;
      if (navigation_bar?.collapse_on_scroll === true || back_to_top_button) {
        this.scrollHandler();
        document.addEventListener('scroll', this.scrollHandler);
      }
    })
  }

  componentWillUnmount() {
    document.removeEventListener('scroll', this.scrollHandler);
  }

  public get scrolledDown() {
    const { layout } = this.props.pageContext;
    const { navigation_bar } = layout;
    if (navigation_bar?.collapse_on_scroll === false) return false;
    return this.state.scrolledDown;
  }

  public render() {
    const { location, pageContext } = this.props;
    const { layout, language, defaultLanguage } = pageContext;
    const { navigation_bar, toastbar, toolbar, back_to_top_button, flyout_promo_box, in_app_back_button, layout_display, footer } = layout;

    const accountpage = pages.account_page
      ? (pages.account_page as TTranslations<{slug: string}>).find(translation => translation.language === language)
      : null;
    
    const isInApp = this.context.isInApp === true;

    return <main className="standard">

      { (toolbar && !isInApp) ? <Toolbar
        language={language}
        languages={languages}
        page_translations={this.props.pageContext.translations}
        pages={pages}
        toolbar={toolbar}
        scrolledDown={this.scrolledDown}
        defaultLanguage={defaultLanguage}
        defaultCurrency={defaultCurrency}
        currencies={currencies}
        onLoginButtonClick={this.toggleLoginModal}
        closeMenuFn={() => this.closeMobileMenu()}
      /> : null }

      {(navigation_bar && !isInApp) ? <NavigationBar
        location={location}
        language={language}
        languages={languages}
        defaultLanguage={defaultLanguage}
        defaultCurrency={defaultCurrency}
        currencies={currencies}
        pages={pages}
        page_translations={this.props.pageContext.translations}
        navigation_bar={navigation_bar}
        has_toolbar_above={!!toolbar}
        scrolled_down={this.scrolledDown}
        mobile_menu_open={this.state.mobileMenuOpen}
        onMobileMenuClick={this.toggleMobileMenu}
        onLoginButtonClick={this.toggleLoginModal}
        flyout_promo_box={flyout_promo_box ? <FlyoutPromoBox flyoutPromoBox={flyout_promo_box} language={language} defaultLanguage={defaultLanguage} /> : null}
      /> : null }

      {(toastbar && !isInApp) ? <ToastBarV2
        scrolledDown={this.scrolledDown}
        toastbar={toastbar}
        language={this.props.language}
        defaultLanguage={this.props.defaultLanguage}
      /> : null }

      {navigation_bar && !isInApp && this.state.mobileMenuOpen
        ? <MobileBackdrop
          open={this.state.mobileMenuOpen}
          navigation_bar={navigation_bar}
          flyout_promo_box={flyout_promo_box ? <FlyoutPromoBox mobile={true} flyoutPromoBox={flyout_promo_box} language={language} defaultLanguage={defaultLanguage} /> : null}
          footer={<MobileToolbarFooterWrapper toolbar={toolbar}>
            {toolbar ? <ToolbarMobileV2
              language={language}
              languages={languages}
              page_translations={this.props.pageContext.translations}
              pages={pages}
              toolbar={toolbar}
              scrolledDown={this.scrolledDown}
              defaultLanguage={defaultLanguage}
              defaultCurrency={defaultCurrency}
              currencies={currencies}
              onLoginButtonClick={this.toggleLoginModal}
              closeMenuFn={() => this.closeMobileMenu()}
            /> : null}
            <NavigationFooter
              language={language}
              languages={languages}
              defaultLanguage={defaultLanguage}
              defaultCurrency={defaultCurrency}
              currencies={currencies}
              pages={pages}
              toolbar={toolbar}
              navigation_bar={navigation_bar}
              page_translations={this.props.pageContext.translations}
              onLoginButtonClick={this.toggleLoginModal}
             />
          </MobileToolbarFooterWrapper>}>
            <MobileNavigation
              location={location}
              language={language}
              defaultLanguage={defaultLanguage}
              items={navigation_bar.navigation_items}
            />
          </MobileBackdrop>
        : null }

      { back_to_top_button
        ? <BackToTopButton button={back_to_top_button} standard_cta_button={pageContext.layout.cta_type} scrolled_down={this.state.scrolledDown}/>
        : null}

      {isInApp
        ? in_app_back_button
          ? <InAppBackButton button={in_app_back_button} standard_cta_button={pageContext.layout.cta_type} hasNavigationBar={Boolean(navigation_bar)}/>
          : <InAppBackButton button={{
              style: pageContext.layout.cta_type || "square",
              background_color: pageContext.layout.cta_background_color || { value: '#000000' },
              icon_color: pageContext.layout.cta_text_color || { value: '#FFFFFF' },
            }} standard_cta_button={pageContext.layout.cta_type || 'square'} hasNavigationBar={Boolean(navigation_bar)} />
        : null}

      {this.props.children}

      { footer && !isInApp
        ? <FooterModule language={language} defaultLanguage={defaultLanguage} footer={footer}/>
        : null}

      <Socket />

      {this.state.showLoginModal ? <LoginModal
          show={true}
          closeFn={() => this.toggleLoginModal(false)}
          onFinishLogin={() => accountpage ? navigate(accountpage.slug) : null}
          doNotAssignGaustaTranslations={true}
          moduleData={provisionalModuleDataBasedOnLanguage(language)}
      /> : null}
    </main>
  }
}